/**
 * AuthScreen — sign-in / sign-up gate consistent with the Qoryn design.
 *
 * Dark obsidian background, restrained cyan accent, single centered card,
 * generous spacing, understated micro-motion via framer-motion. No layout
 * chrome (nav rail, panels) — this is a pre-console gate. Once the user
 * signs in, AuthGate mounts the full FounderConsole.
 *
 * Two modes: sign-in and sign-up. Toggle switches copy + submit label.
 * Errors surface inline (never in a toast — the card is the entire UI).
 */

import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, LogIn, UserPlus } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";

export default function AuthScreen() {
    const { signIn, signUp } = useAuth();
    const [mode, setMode] = useState("signin");    // 'signin' | 'signup'
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [displayName, setDisplayName] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);

    const isSignup = mode === "signup";

    const onSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setNotice(null);
        setBusy(true);
        try {
            if (isSignup) {
                const res = await signUp(email, password, displayName);
                if (res?.user && !res.session) {
                    // Email confirmation required by project settings.
                    setNotice(
                        "Account created. Check your inbox for a confirmation link, " +
                        "then sign in.",
                    );
                    setMode("signin");
                }
            } else {
                await signIn(email, password);
                // On success, AuthGate flips to FounderConsole; no navigation here.
            }
        } catch (err) {
            setError(err?.message || String(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            data-testid="auth-screen"
            className="min-h-screen w-full flex items-center justify-center bg-qoryn-bg text-qoryn-text px-4"
            style={{
                backgroundImage:
                    "radial-gradient(circle at 50% 30%, rgba(0,229,255,0.06), transparent 60%)",
            }}
        >
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
                className="w-full max-w-[420px] rounded-2xl border border-qoryn-border/70 bg-[#0a0d10]/95 backdrop-blur-xl shadow-2xl shadow-black/60 overflow-hidden"
            >
                {/* Brand header */}
                <div className="flex items-center gap-3 px-6 py-5 border-b border-qoryn-border/50">
                    <img
                        src="/qoryn-q-official.png"
                        alt="Qoryn"
                        width={32}
                        height={32}
                        style={{ width: 32, height: 32, objectFit: "contain" }}
                    />
                    <div className="flex flex-col">
                        <span className="font-sans text-[14px] tracking-tight text-qoryn-text">
                            Qoryn
                        </span>
                        <span className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textMuted">
                            Founder Console
                        </span>
                    </div>
                </div>

                <form onSubmit={onSubmit} className="px-6 py-6 space-y-5">
                    <div>
                        <h1 className="font-display text-[22px] font-light tracking-tight text-qoryn-text">
                            {isSignup ? "Create your account" : "Welcome back"}
                        </h1>
                        <p className="mt-1 font-sans text-[13px] text-qoryn-textDim">
                            {isSignup
                                ? "Your operating system for agentic intelligence."
                                : "Sign in to continue your work."}
                        </p>
                    </div>

                    {isSignup && (
                        <label className="block">
                            <span className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textDim">
                                Display name
                            </span>
                            <input
                                data-testid="auth-display-name"
                                type="text"
                                value={displayName}
                                onChange={(e) => setDisplayName(e.target.value)}
                                autoComplete="name"
                                className="mt-1.5 w-full h-10 px-3 rounded-md bg-black/40 border border-qoryn-border focus:border-qoryn-cyan/60 outline-none font-sans text-[14px] text-qoryn-text placeholder:text-qoryn-textMuted transition-colors qoryn-focus"
                                placeholder="How Qoryn addresses you"
                            />
                        </label>
                    )}

                    <label className="block">
                        <span className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textDim">
                            Email
                        </span>
                        <input
                            data-testid="auth-email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            autoComplete="email"
                            className="mt-1.5 w-full h-10 px-3 rounded-md bg-black/40 border border-qoryn-border focus:border-qoryn-cyan/60 outline-none font-sans text-[14px] text-qoryn-text placeholder:text-qoryn-textMuted transition-colors qoryn-focus"
                            placeholder="you@example.com"
                        />
                    </label>

                    <label className="block">
                        <span className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textDim">
                            Password
                        </span>
                        <input
                            data-testid="auth-password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            minLength={8}
                            autoComplete={isSignup ? "new-password" : "current-password"}
                            className="mt-1.5 w-full h-10 px-3 rounded-md bg-black/40 border border-qoryn-border focus:border-qoryn-cyan/60 outline-none font-sans text-[14px] text-qoryn-text placeholder:text-qoryn-textMuted transition-colors qoryn-focus"
                            placeholder={isSignup ? "At least 8 characters" : ""}
                        />
                    </label>

                    {error && (
                        <div
                            data-testid="auth-error"
                            className="rounded-md border border-red-500/40 bg-red-500/[0.06] px-3 py-2 font-mono-ui text-[10px] tracking-wider text-red-300"
                        >
                            {error}
                        </div>
                    )}
                    {notice && (
                        <div
                            data-testid="auth-notice"
                            className="rounded-md border border-qoryn-cyan/40 bg-qoryn-cyan/[0.06] px-3 py-2 font-mono-ui text-[10px] tracking-wider text-qoryn-cyan"
                        >
                            {notice}
                        </div>
                    )}

                    <button
                        data-testid="auth-submit"
                        type="submit"
                        disabled={busy}
                        className="w-full h-11 flex items-center justify-center gap-2 rounded-md bg-qoryn-cyan text-black font-mono-ui text-[11px] tracking-widest uppercase hover:brightness-110 active:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed transition-all qoryn-focus"
                    >
                        {busy ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : isSignup ? (
                            <UserPlus className="h-4 w-4" strokeWidth={2} />
                        ) : (
                            <LogIn className="h-4 w-4" strokeWidth={2} />
                        )}
                        {busy ? "…" : isSignup ? "Create account" : "Sign in"}
                    </button>

                    <div className="text-center">
                        <button
                            type="button"
                            data-testid="auth-toggle-mode"
                            onClick={() => {
                                setMode(isSignup ? "signin" : "signup");
                                setError(null);
                                setNotice(null);
                            }}
                            className="font-mono-ui text-[10px] tracking-widest uppercase text-qoryn-textDim hover:text-qoryn-cyan transition-colors qoryn-focus"
                        >
                            {isSignup
                                ? "Have an account? Sign in"
                                : "New here? Create an account"}
                        </button>
                    </div>
                </form>

                <div className="px-6 py-3 border-t border-qoryn-border/50 bg-black/30">
                    <p className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textMuted text-center">
                        Phase 1B · email · password
                    </p>
                </div>
            </motion.div>
        </div>
    );
}
